import {
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import PasswordInput from "../../components/ui/PasswordInput";
import Text from "../../components/ui/Text";

const SettingsDeleteAccount = ({ isDesktop = false }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleBack = () => {
    navigate("/settings");
  };

  const handleDeleteAccount = () => {
    if (password) {
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmDelete = () => {
    // TODO: Implement account deletion logic
    setShowConfirmDialog(false);
    // Navigate to sign in or home page after deletion
    navigate("/signin");
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };

  const handleForgotPassword = () => {
    // TODO: Implement forgot password logic
    navigate("/forgot-password");
  };

  const isDeleteDisabled = !password;

  // Desktop layout (no container, no back button)
  if (isDesktop) {
    return (
      <>
        <Box>
          {/* Password Confirmation */}
          <Box sx={{ mb: "40px" }}>
            <PasswordInput
              label="Confirm password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Box>

          {/* Forgot Password Link */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <Text
              variant="paragraph3"
              sx={{
                color: "#1890FF",
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "14px",
              }}
              onClick={handleForgotPassword}
            >
              Forgot password?
            </Text>
          </Box>

          {/* Delete Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="primary"
              onClick={handleDeleteAccount}
              disabled={isDeleteDisabled}
              sx={{
                width: "auto",
                minWidth: "120px",
                backgroundColor: "#FF4D4F",
                borderColor: "#FF4D4F",
                "&:hover": {
                  backgroundColor: "#FF7875",
                  borderColor: "#FF7875",
                },
                "&:disabled": {
                  backgroundColor: "#F5F5F5",
                  borderColor: "#D9D9D9",
                  color: "#BFBFBF",
                },
              }}
            >
              Delete my account
            </Button>
          </Box>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmDialog}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: { sx: { borderRadius: "12px", m: 2 } },
          }}
        >
          <DialogTitle sx={{ p: 3, pb: 1 }}>
            <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
              Confirm Account Deletion
            </Text>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 2 }}>
            <Text
              variant="paragraph"
              sx={{ fontSize: "16px", lineHeight: 1.5 }}
            >
              Are you sure you want to delete your account? This action cannot
              be undone.
            </Text>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmDelete}
              sx={{
                flex: 1,
                backgroundColor: "#FF4D4F",
                borderColor: "#FF4D4F",
                "&:hover": {
                  backgroundColor: "#FF7875",
                  borderColor: "#FF7875",
                },
              }}
            >
              Delete Account
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Mobile layout (original)
  return (
    <>
      <Container maxWidth="sm" sx={{ mt: 2 }}>
        {/* Back Button */}
        <Box sx={{ mb: "24px" }}>
          <Button variant="back" onClick={handleBack} />
        </Box>

        {/* Page Header */}
        <Text variant="pageHeader" sx={{ mb: "40px" }}>
          Delete my account
        </Text>

        {/* Password Confirmation */}
        <Box sx={{ mb: "40px" }}>
          <PasswordInput
            label="Confirm password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Box>

        {/* Forgot Password Link */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Text
            variant="paragraph3"
            sx={{
              color: "#1890FF",
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: "14px",
            }}
            onClick={handleForgotPassword}
          >
            Forgot password?
          </Text>
        </Box>

        {/* Delete Button */}
        <Button
          variant="primary"
          onClick={handleDeleteAccount}
          disabled={isDeleteDisabled}
          sx={{
            width: "100%",
            backgroundColor: "#FF4D4F",
            borderColor: "#FF4D4F",
            "&:hover": {
              backgroundColor: "#FF7875",
              borderColor: "#FF7875",
            },
            "&:disabled": {
              backgroundColor: "#F5F5F5",
              borderColor: "#D9D9D9",
              color: "#BFBFBF",
            },
          }}
        >
          Delete my account
        </Button>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2 } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            Are you sure you want to delete your account?
          </Text>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 1 }}>
          <Text variant="paragraph">
            Your information and history will be removed.
          </Text>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Button
            variant="primary"
            onClick={handleConfirmDelete}
            sx={{
              flex: 1,
              backgroundColor: "#FF4D4F",
              borderColor: "#FF4D4F",
              "&:hover": {
                backgroundColor: "#FF7875",
                borderColor: "#FF7875",
              },
            }}
          >
            Yes
          </Button>
          <Button
            variant="secondary"
            onClick={handleCancelDelete}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsDeleteAccount;
